import { router } from '@inertiajs/react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  AdminSummary,
  Submission,
  SubmissionModality,
  SubmissionStatus,
} from '@/types';

interface TeamOverviewProps {
  submission: Submission;
  reviewers: AdminSummary[];
  canEditModality: boolean;
  eventSlug: string;
}

const UNASSIGNED_VALUE = '__unassigned__';

const modalityOptions: Array<{ value: SubmissionModality; label: string }> = [
  { value: 'team', label: 'Team' },
  { value: 'team_looking', label: 'Team Looking' },
  { value: 'solo', label: 'Solo' },
];

const statusColors: Record<SubmissionStatus, string> = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  received: 'bg-blue-100 text-blue-800 border-blue-300',
  priority_waiting: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  asking_self_finance_trip: 'bg-purple-100 text-purple-800 border-purple-300',
  approved: 'bg-green-100 text-green-800 border-green-300',
  onboarding_request: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  onboarding_expired: 'bg-gray-100 text-gray-800 border-gray-300',
  onboarding_complete: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  waiting_list: 'bg-amber-100 text-amber-800 border-amber-300',
  withdrawn: 'bg-orange-100 text-orange-800 border-orange-300',
  archived: 'bg-slate-100 text-slate-800 border-slate-300',
};

const statusLabels: Record<SubmissionStatus, string> = {
  draft: 'Draft',
  received: 'Received',
  priority_waiting: 'Priority Waiting',
  asking_self_finance_trip: 'Asking Self Finance Trip',
  approved: 'Approved',
  onboarding_request: 'Onboarding Request',
  onboarding_expired: 'Onboarding Expired',
  onboarding_complete: 'Onboarding Complete',
  rejected: 'Rejected',
  waiting_list: 'Waiting List',
  withdrawn: 'Withdrawn',
  archived: 'Archived',
};

export function TeamOverview({
  submission,
  reviewers,
  canEditModality,
  eventSlug,
}: TeamOverviewProps) {
  const [isPending, setIsPending] = useState(false);

  const base = `/admin/${eventSlug}/submissions/${submission.id}`;

  const handleReviewerChange = (value: string) => {
    router.patch(
      `${base}/reviewer`,
      { reviewerId: value === UNASSIGNED_VALUE ? '' : value },
      {
        preserveScroll: true,
        onStart: () => setIsPending(true),
        onFinish: () => setIsPending(false),
      },
    );
  };

  const handleModalityChange = (value: string) => {
    router.patch(
      `${base}/modality`,
      { modality: value },
      {
        preserveScroll: true,
        onStart: () => setIsPending(true),
        onFinish: () => setIsPending(false),
      },
    );
  };

  const isDraft = submission.status === 'draft';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="font-medium text-muted-foreground text-sm">
              Status
            </div>
            <Badge
              variant="outline"
              className={statusColors[submission.status]}
            >
              {statusLabels[submission.status]}
            </Badge>
          </div>

          <div>
            <div className="font-medium text-muted-foreground text-sm">
              Cohort
            </div>
            <div className="text-sm capitalize">{submission.cohort}</div>
          </div>

          <div>
            <div className="font-medium text-muted-foreground text-sm">
              Modality
            </div>
            {canEditModality && isDraft ? (
              <Select
                value={submission.modality}
                disabled={isPending}
                onValueChange={handleModalityChange}
              >
                <SelectTrigger className="h-8 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {modalityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-sm capitalize">{submission.modality}</div>
            )}
          </div>

          <div>
            <div className="font-medium text-muted-foreground text-sm">
              Country
            </div>
            <div className="text-sm">{submission.country}</div>
          </div>

          <div className="col-span-2">
            <div className="mb-1 font-medium text-muted-foreground text-sm">
              Reviewer
            </div>
            <Select
              value={submission.reviewerId ?? UNASSIGNED_VALUE}
              disabled={isPending}
              onValueChange={handleReviewerChange}
            >
              <SelectTrigger className="h-8 w-full">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED_VALUE}>Unassigned</SelectItem>
                {reviewers.map((reviewer) => (
                  <SelectItem key={reviewer.id} value={reviewer.id}>
                    {reviewer.fullName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <div className="font-medium text-muted-foreground text-sm">
              Submission ID
            </div>
            <div className="font-mono text-muted-foreground text-xs">
              {submission.id}
            </div>
          </div>

          <div className="col-span-2">
            <div className="font-medium text-muted-foreground text-sm">
              Submitted At
            </div>
            <div className="text-sm">
              {new Date(submission.submittedAt).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
